import React, { useEffect } from "react";
import ReactDom from "react-dom";
import { useNavigate } from "react-router-dom";

import styles from "./actionDetailsModal.module.css";
import { getActionPlan } from "./actionPlans";
import { formatNumberToCurrency, formatShortDate } from "../../utils/formatter";

const PRIORITY_LABEL = { 3: "Urgente", 2: "Normal", 1: "Baixa" };

// De onde saiu o valor em jogo de uma ação de evasão: chance calibrada (ou taxa observada, nas ações antigas) ou proxy.
const describeValueBasis = (item, evidence) => {
  if (item.type !== "EVASAO_RISCO_ALTO" || !evidence) {
    return null;
  }
  const monthly = formatNumberToCurrency(Number(evidence.monthlyIssued || 0));
  if (evidence.valueBasis === "observed" && evidence.dropoutRate != null) {
    const pct = Math.round(evidence.dropoutRate * 100);
    const months = evidence.monthsRemaining || 0;
    // ações novas trazem a chance calibrada pela curva; as antigas, a taxa observada de um ciclo
    const basis = evidence.rateMethod === "calibrated"
      ? `${pct}% de chance estimada de evadir com este score`
      : `${pct} de cada 100 alunos com este score evadiram`;
    return `${basis} · ${monthly}/mês × ${months} ${months === 1 ? "mês restante" : "meses restantes"}`;
  }
  return `${monthly}/mês × 6 meses · sem histórico de evasão medido ainda`;
};

const ActionDetailsModal = ({ item, evidence, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!item) {
    return null;
  }

  const plan = getActionPlan(item.type);
  const students = Array.isArray(evidence?.students) ? evidence.students : [];
  const reasons = Array.isArray(evidence?.reasons) ? evidence.reasons : [];
  const hiddenStudents = (evidence?.count || 0) - students.length;
  const isReenrollment = item.type === "REMATRICULA";
  const valueBasis = describeValueBasis(item, evidence);

  return ReactDom.createPortal(
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{item.title}</h3>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Prioridade</span>
            <span className={styles.metaValue}>{PRIORITY_LABEL[item.priority] || "Normal"}</span>
          </div>
          {item.estimatedValue > 0 && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Valor em jogo</span>
              <span className={styles.metaValue}>{formatNumberToCurrency(item.estimatedValue)}</span>
              {valueBasis && <span className={styles.metaHint}>{valueBasis}</span>}
            </div>
          )}
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Detectada em</span>
            <span className={styles.metaValue}>{formatShortDate(item.createdAt) || "-"}</span>
          </div>
        </div>

        <div className={styles.body}>
          {plan && (
            <section className={styles.block}>
              <h4 className={styles.blockTitle}>Como resolver</h4>
              <p className={styles.planSummary}>{plan.summary}</p>
              <ul className={styles.steps}>
                {plan.steps.map((step, index) => (
                  <li key={step} className={styles.step}>
                    <span className={styles.stepNumber}>{index + 1}</span>
                    <span className={styles.stepText}>{step}</span>
                  </li>
                ))}
              </ul>
              {plan.route && (
                <button
                  type="button"
                  className={styles.routeLink}
                  onClick={() => {
                    onClose();
                    navigate(plan.route);
                  }}
                >
                  {plan.routeLabel}
                </button>
              )}
            </section>
          )}

          {reasons.length > 0 && (
            <section className={styles.block}>
              <h4 className={styles.blockTitle}>Motivos do risco</h4>
              <div className={styles.reasons}>
                {reasons.map((reason) => {
                  const text = typeof reason === "string"
                    ? reason
                    : reason.description || reason.reason || JSON.stringify(reason);

                  return (
                    <span key={text} className={styles.reasonChip}>
                      {text}
                    </span>
                  );
                })}
              </div>
            </section>
          )}

          {students.length > 0 && (
            <section className={styles.block}>
              <h4 className={styles.blockTitle}>Alunos ({students.length})</h4>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Aluno</th>
                      <th>Curso</th>
                      <th>{isReenrollment ? "Fim do período" : "Vencimento"}</th>
                      <th>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student.studentId}>
                        <td>{student.studentId}</td>
                        <td>{student.course || "-"}</td>
                        <td>{formatShortDate(student.dueDate || student.endDate) || "-"}</td>
                        <td>{formatNumberToCurrency(Number(student.value ?? student.monthlyIssued ?? 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {hiddenStudents > 0 && (
                <span className={styles.more}>
                  e mais {hiddenStudents} aluno(s) não listados aqui. Use a tela indicada acima para a lista completa.
                </span>
              )}
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ActionDetailsModal;
